import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import Button from './Button';

type ConfirmationDialogProps = {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  description: string;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: 'primary' | 'danger';
  loading?: boolean;
};

export default function ConfirmationDialog({
  open,
  onClose,
  onConfirm,
  title,
  description,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  variant = 'primary',
  loading = false,
}: ConfirmationDialogProps) {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const content = (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] flex items-center justify-center p-4"
          style={{ background: 'var(--c-charcoal-60)', backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)' }}
          role="dialog"
          aria-modal="true"
          onClick={(e) => {
            if (e.target === e.currentTarget && !loading) onClose();
          }}
        >
          <motion.div
            initial={{ scale: 0.95, y: 10, opacity: 0 }}
            animate={{ scale: 1, y: 0, opacity: 1 }}
            exit={{ scale: 0.95, y: 10, opacity: 0 }}
            transition={{ type: 'spring', duration: 0.3 }}
            className="w-full max-w-sm p-6"
            style={{ background: 'var(--c-cream)', borderRadius: 'var(--r-lg)', boxShadow: 'var(--sh-lifted)', border: '1px solid var(--rule)' }}
            onClick={(e) => { e.stopPropagation(); }}
          >
            <h3 className="mb-2" style={{ color: 'var(--ink)', font: `600 var(--fs-body)/1.3 var(--f-text)` }}>{title}</h3>
            <p className="mb-6 whitespace-pre-line" style={{ color: 'var(--ink-muted)', fontSize: 'var(--fs-small)' }}>{description}</p>

            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={onClose} disabled={loading} size="sm">
                {cancelLabel}
              </Button>
              <Button
                variant={variant === 'danger' ? 'primary' : 'primary'}
                onClick={onConfirm}
                disabled={loading}
                size="sm"
                className={variant === 'danger' ? 'bg-rose-600 hover:bg-rose-700' : ''}
              >
                {loading ? 'Processing...' : confirmLabel}
              </Button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );

  if (!mounted) return null;
  return createPortal(content, document.body);
}
